/**
 * Provides the CSRF token to the application API handlers
 */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import decodeJson from '@seccon/shared/utils/decodeJson';

const CSRFContext = React.createContext(null);

export const CSRFProvider = ({ children }) => {
  const [csrf, setCsrf] = useState(null);

  // Retrieves the deadline from the token string
  function getDelay (token) {
    const now = new Date().getTime();
    let deadline = now;

    try {
      const timestamp = +atob(token.split('$')[1]);

      if (!isNaN(timestamp)) {
        deadline = new Date(timestamp).getTime();
      }
    } catch (err) {
      deadline += 0; // if we couldn't read the token, set the deadline to run right now
      console.error('An error occurred trying to parse the csrf token', err);
    }

    // We want to run the refresh 30 seconds before the deadline
    // Find the delay by taking the deadline - now - 30 seconds
    return Math.max(deadline - now - 30000, 1);
  }

  // Refreshes the csrf token
  async function refreshToken (oldToken) {
    const response = await fetch('/login/refreshToken', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'anti-csrf-token': oldToken
      }
    });

    // Fail silently
    if (!response.ok) {
      return;
    }

    const data = await decodeJson(response);

    setCsrf(data.token);

    setTimeout(() => refreshToken(data.token), getDelay(data.token));
  }

  // On mount, retrieve the token value from the meta tags and set a timer to refresh the token
  useEffect(() => {
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

    // If the token hasn't been implemented yet on the page, exit early (nothing to do here)
    if (!token) {
      return;
    }

    setCsrf(token);

    setTimeout(() => refreshToken(token), getDelay(token));
  }, []);

  return (
    <CSRFContext.Provider value={csrf}>
      {children}
    </CSRFContext.Provider>
  );
};

CSRFProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.arrayOf(PropTypes.element)]).isRequired
};

export default CSRFContext;
